import IColDef from "components/UI/AcxDataGrid/IColDef";
import { toJS } from "mobx";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { SignalReportDatum } from "models/Reporting/ReportDatum";
import { Box, Grid, Stack } from "@mui/material";
import AcxProgress from "components/UI/AcxDataGrid/Formatters/AcxProgress";
import theme from "Theme/AppTheme";
import AcxChip from "components/UI/AcxChip";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import AcxClickableCell from "components/UI/AcxDataGrid/AcxClickableCell";
import moment from "moment";
import { signalsChartColorScheme } from "components/UI/Visualization/GraphColorSchemes";
import { GridBuilder } from "./Types";
import type ChartCardStore from "../ChartCard/ChartCardStore";
import { SignalsTrendingIndicator } from "../SignalsTrendingIndicator";
import { SignalsTemporalLabels } from "components/Signals/SignalsModels";
import { v4 as uuidv4 } from "uuid";
import { getClassifierOptionsForCategory } from "../ChartCard/ChartControls/classifierUtils";
import { getXDateRangeLabel } from "../ChartCard/dateBucketLabels";

export const buildTopicPrevalenceGrid: GridBuilder = (
    dataGridStore,
    gridConfig,
    currentData,
    chartCardStore,
) => {
    // Build the grid using the currentData
    const labels = currentData.map((datum) => datum.grouping);

    const uniqueLabels = Array.from(new Set(labels));
    dataGridStore.rows = uniqueLabels.map((i) => {
        return {
            topicLabel: i,
            id: currentData.find((d) => d.grouping === i)?.grouping_identifier,
        };
    });

    const initColCount = dataGridStore.columns.length;

    const rows = populateDataGrid({
        responseData: currentData,
        rowField: "topicLabel",
        dataGridStore: dataGridStore,
        initColCount,
        useTotalConversationsForPercent: true,
        respectDataOrder: true,
        totalConversations: chartCardStore.optionsStore.totalConversationsValue,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });

    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);

    if (gridConfig.followPagination) {
        dataGridStore.setRows(
            chartCardStore.getPaginatedDataGridRows(dataGridStore.allRows),
        );
    }
};

export const buildContactTypesOverTimeGrid: GridBuilder = (
    dataGridStore,
    gridConfig,
    currentData,
    chartCardStore,
) => {
    // Build the grid using the currentData
    const initColCount = dataGridStore.columns.length;

    dataGridStore.rows = buildContactTypeRows(chartCardStore);

    const rows = populateDataGrid({
        responseData: currentData,
        rowField: "label",
        dataGridStore: dataGridStore,
        initColCount,
        respectDataOrder: true,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });

    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);

    if (gridConfig.followPagination) {
        dataGridStore.setRows(
            chartCardStore.getPaginatedDataGridRows(dataGridStore.allRows),
        );
    }
};

// Rows for contact-type grids. Always show a row for every org contact type,
// sourced dynamically from `customerTypes` (no hardcoded list). populateDataGrid
// fills values for the types present in the current response and leaves the rest
// blank, and the grid is rebuilt every refresh so rows always reflect the latest
// data (and clear when a type drops out of the range).
const buildContactTypeRows = (
    chartCardStore: ChartCardStore,
): { id: string; label: string }[] => {
    return chartCardStore.optionsStore.customerTypes
        .filter((ct) => !!ct.sourceName)
        .map((ct) => ({
            id: ct.sourceName as string,
            label: ct.name,
        }));
};

export const buildEddyByContactTypeOverTimeGrid: GridBuilder = (
    dataGridStore,
    gridConfig,
    currentData,
    chartCardStore,
) => {
    const initColCount = dataGridStore.columns.length;

    dataGridStore.rows = buildContactTypeRows(chartCardStore);

    const rows = populateDataGrid({
        responseData: currentData,
        rowField: "label",
        dataGridStore: dataGridStore,
        initColCount,
        respectDataOrder: true,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });

    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);

    if (gridConfig.followPagination) {
        dataGridStore.setRows(
            chartCardStore.getPaginatedDataGridRows(dataGridStore.allRows),
        );
    }
};

export const buildClassifierOverTimeGrid: GridBuilder = (
    dataGridStore,
    gridConfig,
    currentData,
    chartCardStore,
) => {
    const initColCount = dataGridStore.columns.length;
    const classifierCategoryId = chartCardStore.selectedIds[
        "classifierCategory"
    ] as string | undefined;
    const classifiers = getClassifierOptionsForCategory(
        chartCardStore,
        classifierCategoryId,
    );
    dataGridStore.rows = classifiers;

    const rows = populateDataGrid({
        responseData: currentData,
        rowField: "name",
        dataGridStore,
        initColCount,
        respectDataOrder: true,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });

    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);

    if (gridConfig.followPagination) {
        dataGridStore.setRows(
            chartCardStore.getPaginatedDataGridRows(dataGridStore.allRows),
        );
    }
};

export const buildEddyByClassifierOverTimeGrid: GridBuilder = (
    dataGridStore,
    gridConfig,
    currentData,
    chartCardStore,
) => {
    const initColCount = dataGridStore.columns.length;
    const classifierCategoryId = chartCardStore.selectedIds[
        "classifierCategory"
    ] as string | undefined;
    const classifiers = getClassifierOptionsForCategory(
        chartCardStore,
        classifierCategoryId,
    );
    dataGridStore.rows = classifiers;

    const rows = populateDataGrid({
        responseData: currentData,
        rowField: "name",
        dataGridStore,
        initColCount,
        addTotalConversationsPercent: true,
        totalConversations: chartCardStore.optionsStore.totalConversationsValue,
        respectDataOrder: true,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });

    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);

    if (gridConfig.followPagination) {
        dataGridStore.setRows(
            chartCardStore.getPaginatedDataGridRows(dataGridStore.allRows),
        );
    }
};

export const buildAverageScoreByQuestionOverTimeGrid: GridBuilder = (
    dataGridStore,
    gridConfig,
    currentData,
    chartCardStore,
) => {
    const initColCount = dataGridStore.columns.length;
    const selectedModuleId = chartCardStore.selectedIds["module"] as
        | string
        | undefined;

    const module =
        chartCardStore.optionsStore.orgModules.find(
            (m) => m.id === selectedModuleId,
        ) ?? chartCardStore.optionsStore.orgModules[0];

    const questions = module?.questions ?? [];
    // datum.grouping from the backend is ShortQuestionText (the query aliases
    // questions."ShortQuestionText" AS "QuestionText").
    dataGridStore.rows = questions;

    const rows = populateDataGrid({
        responseData: currentData,
        rowField: "shortQuestionText",
        dataGridStore,
        initColCount,
        addPercentSigns: true,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });
    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);

    if (gridConfig.followPagination) {
        dataGridStore.setRows(
            chartCardStore.getPaginatedDataGridRows(dataGridStore.allRows),
        );
    }
};

export const buildAverageScoreByModuleOverTimeGrid: GridBuilder = (
    dataGridStore,
    gridConfig,
    currentData,
    chartCardStore,
) => {
    const initColCount = dataGridStore.columns.length;
    const canSelectAll = chartCardStore.config.capabilities.canSelectAllModules;
    const scoredModules = chartCardStore.optionsStore.orgScoredModules;
    const allModules = chartCardStore.optionsStore.orgModules;
    const modules = canSelectAll
        ? allModules
        : scoredModules?.length
        ? scoredModules
        : allModules;

    const rowsToUse =
        modules?.map((module) => ({
            ...module,
            displayName: module.displayName ?? module.name,
        })) ?? [];

    dataGridStore.rows = rowsToUse;

    // Rows list every module the viewer can pick, but only the selected ones were
    // requested. When the selection is explicitly empty the backend still answers with
    // its default module set, so drop it here rather than filling cells nobody asked for.
    const selectedModuleIds = chartCardStore.selectedIds.modules;
    const dataForSelection =
        Array.isArray(selectedModuleIds) && selectedModuleIds.length === 0
            ? []
            : currentData;

    const rows = populateDataGrid({
        responseData: dataForSelection,
        rowField: "displayName",
        dataGridStore,
        initColCount,
        addPercentSigns: true,
        useGroupingId: true,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });
    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);

    if (gridConfig.followPagination) {
        dataGridStore.setRows(
            chartCardStore.getPaginatedDataGridRows(dataGridStore.allRows),
        );
    }
};

export const buildSafetyEventsOverTimeGrid: GridBuilder = (
    dataGridStore,
    _gridConfig,
    currentData,
    chartCardStore,
) => {
    const initColCount = dataGridStore.columns.length;
    dataGridStore.rows = [
        { id: "Identified", safetyEventResult: "Identified" },
        { id: "Not Identified", safetyEventResult: "Not Identified" },
        { id: "Acknowledged", safetyEventResult: "Acknowledged" },
        { id: "Not Acknowledged", safetyEventResult: "Not Acknowledged" },
    ];

    const rows = populateDataGrid({
        responseData: currentData,
        rowField: "id",
        dataGridStore,
        initColCount,
        useSEIdentifiedCount: true,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });

    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);
};

export const buildHipaaOverTimeGrid: GridBuilder = (
    dataGridStore,
    _gridConfig,
    currentData,
    chartCardStore,
) => {
    const initColCount = dataGridStore.columns.length;
    const hipaaOptions = chartCardStore.optionsStore.hipaaOptions ?? [];

    dataGridStore.rows = hipaaOptions.map((option) => ({
        id: option.id,
        hipaaResult: option.label,
    }));

    const rows = populateDataGrid({
        responseData: currentData,
        rowField: "id",
        dataGridStore,
        initColCount,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });

    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);
};

export const buildQuestionResponseOverTimeGrid: GridBuilder = (
    dataGridStore,
    gridConfig,
    currentData,
    chartCardStore,
) => {
    const initColCount = dataGridStore.columns.length;
    const selectedModuleId = chartCardStore.selectedIds["module"] as
        | string
        | undefined;
    const module =
        chartCardStore.optionsStore.orgModules.find(
            (m) => m.id === selectedModuleId,
        ) ?? chartCardStore.optionsStore.orgModules[0];
    const selectedQuestionId = chartCardStore.selectedIds["question"] as
        | string
        | undefined;

    const question =
        module?.questions.find((q) => q.id === selectedQuestionId) ??
        module?.questions[0];

    const tags = question?.tags ?? [];

    dataGridStore.rows = tags;

    const rows = populateDataGrid({
        responseData: currentData,
        rowField: "value",
        dataGridStore,
        initColCount,
        respectDataOrder: true,
        temporalGrain: chartCardStore.temporalGrain,
        startDate: chartCardStore.startDate,
        endDate: chartCardStore.endDate,
    });

    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);

    if (gridConfig.followPagination) {
        dataGridStore.setRows(
            chartCardStore.getPaginatedDataGridRows(dataGridStore.allRows),
        );
    }
};

export function getSignalsColumns(
    type:
        | "Classifier With Eddy"
        | "Question Response"
        | "Classifier Prevalence"
        | "Hipaa"
        | "Module Score"
        | "Question"
        | "Safety Events"
        | "Topics"
        | "Contact Type"
        | "Eddy by Contact Type"
        | "Trending Topics"
        | "Model Performance",
): IColDef[] {
    const classifierWithEddyColumns: IColDef[] = [
        {
            headerName: "Classifier",
            field: "name",
            type: "string",
            flex: 1,
            sortable: false,
        },
        {
            headerName: "Category",
            field: "classifierCategory",
            valueGetter: (value, row, column, apiRef) => {
                return row.classifierCategory?.name ?? "";
            },
            flex: 1,
            sortable: false,
        },
        {
            headerName: "Eddy Count",
            field: "count",
            type: "number",
            headerAlign: "center",
            flex: 1,
            align: "center",
            sortable: false,
        },
        {
            headerName: "Total Count",
            field: "totalCount",
            type: "number",
            headerAlign: "center",
            flex: 1,
            align: "center",
            sortable: false,
        },
        {
            headerName: "Eddy %",
            field: "percent",
            type: "number",
            headerAlign: "center",
            flex: 1,
            align: "center",
            sortable: false,
        },
        {
            headerName: "% of Total Conversations",
            field: "conversationsPercent",
            type: "number",
            headerAlign: "center",
            flex: 1.1,
            align: "center",
            sortable: false,
        },
    ];

    const classifierPrevalenceColumns: IColDef[] = [
        {
            headerName: "Classifier",
            field: "name",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Category",
            field: "classifierCategory",
            valueGetter: (value, row, column, apiRef) => {
                return row.classifierCategory?.name ?? "";
            },
            flex: 1,
        },
        {
            headerName: "Count",
            field: "count",
            type: "number",
            headerAlign: "center",
            flex: 1,
            align: "center",
        },
        {
            headerName: "%",
            field: "percent",
            type: "number",
            headerAlign: "center",
            flex: 1,
            align: "center",
        },
    ];

    const hipaaColumns: IColDef[] = [
        {
            field: "hipaaResult",
            headerName: "Model Result",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Count",
            field: "count",
            type: "number",
            headerAlign: "center",
            flex: 1,
            align: "center",
        },
        {
            headerName: "%",
            field: "percent",
            type: "number",
            headerAlign: "center",
            flex: 1,
            align: "center",
        },
    ];

    const questionResponseColumns: IColDef[] = [
        {
            headerName: "Answers",
            field: "value",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Eddy Count",
            field: "count",
            type: "number",
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "Total Count",
            field: "totalCount",
            type: "number",
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "Eddy %",
            field: "percent",
            type: "number",
            headerAlign: "center",
            align: "center",
            flex: 1,
        },
    ];

    const moduleScoreColumns: IColDef[] = [
        {
            headerName: "Modules",
            field: "displayName",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Avg Score",
            field: "avgScore",
            type: "number",
            headerAlign: "center",
            align: "center",
            flex: 1,
        },
    ];

    const safetyEventsColumns: IColDef[] = [
        {
            field: "safetyEventResult",
            headerName: "Model Result",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Count",
            field: "count",
            type: "number",
            headerAlign: "center",
            flex: 1,
            align: "center",
        },
        {
            headerName: "%",
            field: "percent",
            type: "number",
            headerAlign: "center",
            flex: 1,
            align: "center",
        },
    ];

    const questionColumns: IColDef[] = [
        {
            headerName: "Questions",
            field: "shortQuestionText",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Avg Score",
            field: "avgScore",
            type: "number",
            headerAlign: "center",
            align: "center",
            flex: 1,
        },
    ];

    const topicsColumns: IColDef[] = [
        {
            headerName: "Theme",
            field: "topicLabel",
            type: "string",
            flex: 1,
            sortable: false,
        },
        {
            headerName: "Volume",
            field: "count",
            type: "number",
            headerAlign: "center",
            align: "center",
            flex: 1,
            sortable: false,
        },
        {
            headerName: "%",
            field: "percent",
            type: "number",
            headerAlign: "center",
            align: "center",
            flex: 1,
            sortable: false,
        },
    ];

    const contactsColumns: IColDef[] = [
        {
            field: "label",
            headerName: "Caller Type",
            type: "string",
            flex: 1,
        },
        {
            field: "count",
            headerName: "Volume",
            type: "number",
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "percent",
            headerName: "%",
            type: "number",
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
    ];

    const eddyByContactTypeColumns: IColDef[] = [
        {
            field: "label",
            headerName: "Caller Type",
            type: "string",
            flex: 1,
        },
        {
            field: "count",
            headerName: "Eddy Count",
            type: "number",
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "totalCount",
            headerName: "Total Count",
            type: "number",
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "percent",
            headerName: "Eddy %",
            type: "number",
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
    ];

    const trendingCols: IColDef[] = [
        {
            headerName: "Topic",
            field: "topicName",
            type: "string",
            flex: 1,
            sortable: false,
            cellClassName: "clickable-cell",
            renderCell: (params: GridRenderCellParams) => {
                return <AcxClickableCell>{params.value}</AcxClickableCell>;
            },
        },
        {
            headerName: "Theme",
            field: "themeName",
            renderCell: (params) => {
                const value = params.value?.toString();
                if (!value) return null;
                return (
                    <Grid
                        item
                        container
                        style={{ height: "100%" }}
                        alignItems={"center"}
                    >
                        <AcxChip size="small" color={"gray"} label={value} />
                    </Grid>
                );
            },
            flex: 1,
            sortable: false,
        },
        {
            headerName: "Change",
            field: "percentChange",
            renderCell: (params: GridRenderCellParams) => {
                let value = Number(params.value);
                let isNew = params.row.isNew as boolean;
                let isToZero = params.row.isToZero as boolean;
                return (
                    <SignalsTrendingIndicator
                        inputValue={value}
                        isNew={isNew}
                        isToZero={isToZero}
                        isNegativeRed
                    />
                );
            },
            flex: 1,
            sortable: false,
        },
        {
            headerName: "Percent",
            field: "percent",
            renderCell: (params) => {
                return (
                    <Grid
                        container
                        item
                        xs={12}
                        wrap="nowrap"
                        justifyContent={"center"}
                        alignItems={"center"}
                        gap={1}
                    >
                        <Grid item xs={10}>
                            <AcxProgress
                                key={params.id}
                                percentComplete={params.value?.toString()}
                                backgroundColor={theme.palette.primary.main}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            {params.value}%
                        </Grid>
                    </Grid>
                );
            },
            flex: 1,
            sortable: false,
        },
        {
            headerName: "Volume",
            field: "volume",
            type: "number",
            flex: 1,
            sortable: false,
        },
    ];

    const modelPerformanceColumns: IColDef[] = [
        {
            field: "resultType",
            headerName: "Result Type",
            type: "string",
            flex: 2,
            renderCell: (params: GridRenderCellParams) => {
                if (params.row.resultType === "Total") {
                    return <strong>{params.row.resultType}</strong>;
                }
                return (
                    <Stack
                        direction="row"
                        justifyContent={"space-between"}
                        alignItems={"center"}
                    >
                        {params.row.resultType}
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "100%",
                                backgroundColor:
                                    signalsChartColorScheme[
                                        params.row.index %
                                            signalsChartColorScheme.length
                                    ],
                                marginRight: 2,
                                marginY: 0.5,
                            }}
                        />
                    </Stack>
                );
            },
        },
        {
            field: "volume",
            headerName: "Volume",
            type: "number",
            align: "left",
            headerAlign: "left",
            width: 92,
        },
        {
            field: "percent",
            headerName: "%",
            type: "string",
            width: 92,
        },
        {
            field: "modelResult",
            headerName: "Model Result",
            type: "string",
            flex: 1,
        },
        {
            field: "humanResult",
            headerName: "Human Result",
            type: "string",
            flex: 1,
        },
    ];

    let columns: IColDef[] = [];

    if (type === "Classifier With Eddy") {
        columns = classifierWithEddyColumns;
    } else if (type === "Question Response") {
        columns = questionResponseColumns;
    } else if (type === "Classifier Prevalence") {
        columns = classifierPrevalenceColumns;
    } else if (type === "Hipaa") {
        columns = hipaaColumns;
    } else if (type === "Module Score") {
        columns = moduleScoreColumns;
    } else if (type === "Question") {
        columns = questionColumns;
    } else if (type === "Safety Events") {
        columns = safetyEventsColumns;
    } else if (type === "Topics") {
        columns = topicsColumns;
    } else if (type === "Contact Type") {
        columns = contactsColumns;
    } else if (type === "Eddy by Contact Type") {
        columns = eddyByContactTypeColumns;
    } else if (type === "Trending Topics") {
        columns = trendingCols;
    } else if (type === "Model Performance") {
        columns = modelPerformanceColumns;
    }
    return columns;
}

export function populateDataGrid(args: {
    responseData: SignalReportDatum[];
    rowField: string;
    dataGridStore: AcxDataGridStore;
    initColCount: number;
    addPercentSigns?: boolean;
    useTotalConversationsForPercent?: boolean;
    addTotalConversationsPercent?: boolean;
    useSEIdentifiedCount?: boolean;
    useGroupingId?: boolean;
    sortByEstimatedPercent?: boolean;
    respectDataOrder?: boolean;
    totalConversations?: number;
    temporalGrain?: SignalsTemporalLabels;
    startDate?: moment.Moment;
    endDate?: moment.Moment;
}): any[] {
    const dateGroupings = args.responseData.map((datum) => datum.x);

    const parseTemporalGrouping = (grouping: string) =>
        moment(
            grouping,
            [moment.ISO_8601, "MMM DD, YYYY", "MMM D, YYYY"],
            true,
        );

    const uniqueDateGroupings = Array.from(new Set(dateGroupings));
    const initialGroupingOrder = new Map(
        uniqueDateGroupings.map((group, index) => [group, index]),
    );

    uniqueDateGroupings.sort((groupA, groupB) => {
        const parsedA = parseTemporalGrouping(groupA ?? "");
        const parsedB = parseTemporalGrouping(groupB ?? "");
        const parsedAIsValid = parsedA.isValid();
        const parsedBIsValid = parsedB.isValid();

        if (parsedAIsValid && parsedBIsValid) {
            return parsedA.valueOf() - parsedB.valueOf();
        }

        if (parsedAIsValid) {
            return -1;
        }

        if (parsedBIsValid) {
            return 1;
        }

        return (
            (initialGroupingOrder.get(groupA) ?? 0) -
            (initialGroupingOrder.get(groupB) ?? 0)
        );
    });

    const newCols: IColDef[] = uniqueDateGroupings.map((group) => {
        let momentFormatString = "MMM D, YYYY";
        if (args.startDate?.year() === args.endDate?.year()) {
            momentFormatString = "MMM D";
        }
        const headerLabel = getXDateRangeLabel(
            group,
            args.temporalGrain ?? "Daily",
            args.startDate,
            args.endDate,
            momentFormatString,
        );

        return {
            field: group ?? "",
            type: "number",
            flex: 1,
            align: "center",
            headerAlign: "center",
            sortable: false,
            headerName: headerLabel,
        };
    });

    const rowNamesSelected = args.responseData.map((datum) => datum.grouping);

    const rowIdsSelected = args.responseData.map(
        (datum) => datum.grouping_identifier,
    );

    const initCols = args.dataGridStore.columns.slice(0, args.initColCount);

    const rowCopy = [...toJS(args.dataGridStore.rows)];

    if (args.respectDataOrder) {
        const orderedKeys: string[] = [];
        args.responseData.forEach((datum) => {
            const key = args.useGroupingId
                ? datum.grouping_identifier ?? datum.grouping
                : datum.grouping ?? datum.grouping_identifier;
            if (key && !orderedKeys.includes(key)) {
                orderedKeys.push(key);
            }
        });

        const orderMap = new Map<string, number>(
            orderedKeys.map((key, index) => [key, index]),
        );

        rowCopy.sort((a, b) => {
            const keyA = args.useGroupingId ? a.id : a[args.rowField];
            const keyB = args.useGroupingId ? b.id : b[args.rowField];

            const orderA = orderMap.get(`${keyA}`) ?? Number.MAX_SAFE_INTEGER;
            const orderB = orderMap.get(`${keyB}`) ?? Number.MAX_SAFE_INTEGER;

            if (orderA === orderB) return 0;
            return orderA - orderB;
        });
    }

    if (args.sortByEstimatedPercent) {
        rowCopy.sort((a, b) => {
            if (a.estimatedPercentageOfTotal > b.estimatedPercentageOfTotal) {
                return -1;
            } else {
                return 1;
            }
        });
    }
    const sortedRows = rowCopy;

    const computedRows = sortedRows.map((row) => {
        row["count"] = 0;
        row["percent"] = 0;

        let totalCountByRow = {};

        args.responseData.forEach((datum) => {
            const setRowData = () => {
                const isSyntheticScoreGap =
                    args.addPercentSigns && datum.isSyntheticTemporalGap;

                if (isSyntheticScoreGap) {
                    row[datum.x] = null;
                    return;
                }

                row[datum.x] = datum.y;
                if (args.addPercentSigns) {
                    row[datum.x] = datum.y.toFixed(1) + "%";
                }
                row["count"] += datum.y;
                if (!totalCountByRow[row[args.rowField]]) {
                    totalCountByRow[row[args.rowField]] =
                        datum.y_totalcount ?? 0;
                } else {
                    totalCountByRow[row[args.rowField]] +=
                        datum.y_totalcount ?? 0;
                }
            };
            if (args.useGroupingId) {
                if (row.id === datum.grouping_identifier) {
                    setRowData();
                }
            } else {
                if (row[args.rowField] === datum.grouping) {
                    setRowData();
                }
            }
        });

        row["totalCount"] = totalCountByRow[row[args.rowField]];

        const numOfColWithData = uniqueDateGroupings.filter(
            (group) => row[group] !== null && row[group] !== undefined,
        ).length;

        if (numOfColWithData) {
            row["avgScore"] =
                (row["count"] / numOfColWithData).toFixed(1) + "%";
        }

        if (args.useTotalConversationsForPercent) {
            row["percent"] =
                ((row["count"] / (args.totalConversations ?? 1)) * 100).toFixed(
                    1,
                ) + "%";
        } else {
            const percentVal =
                (row["count"] / totalCountByRow[row[args.rowField]]) * 100;

            row["percent"] = isNaN(percentVal)
                ? "N/A"
                : percentVal.toFixed(1) + "%";
        }

        if (args.addTotalConversationsPercent === true) {
            row["conversationsPercent"] =
                (
                    (row["totalCount"] / (args.totalConversations ?? 1)) *
                    100
                ).toFixed(1) + "%";
        }

        if (args.useSEIdentifiedCount) {
            const identifiedCount = args.responseData
                .filter((datum) => datum.grouping === "Identified")
                .reduce((previous, current) => {
                    return previous + (current.y ?? 0);
                }, 0);

            if (
                row.safetyEventResult &&
                (row.safetyEventResult === "Acknowledged" ||
                    row.safetyEventResult === "Not Acknowledged")
            ) {
                row["percent"] =
                    ((row["count"] / identifiedCount) * 100).toFixed(1) + "%";
            }
        }

        // clear out computed data for rows that don't appear in the response to avoid stale values
        if (args.useGroupingId) {
            if (!rowIdsSelected.includes(row.id)) {
                row["count"] = null;
                row["percent"] = null;
                row["avgScore"] = null;
            }
        } else {
            if (!rowNamesSelected.includes(row[args.rowField])) {
                row["count"] = null;
                row["percent"] = null;
                row["avgScore"] = null;
            }
        }

        return row;
    });

    args.dataGridStore.rows = computedRows;
    args.dataGridStore.setColumns([...initCols, ...newCols]);

    return args.dataGridStore.rows;
}

export const buildModelPerformanceGrid: GridBuilder = (
    dataGridStore,
    _gridConfig,
    currentData,
) => {
    const rows =
        currentData?.map((datum, index) => {
            const { modelResult, humanResult } = resultTypeMapping(
                (datum.x as string) ?? "",
            );
            return {
                resultType: datum.x,
                volume: datum.y,
                percent:
                    datum.y_percent != null
                        ? `${(datum.y_percent * 100).toFixed(1)}%`
                        : "-",
                modelResult,
                humanResult,
                index,
                id: uuidv4(),
            };
        }) ?? [];

    if (rows.length > 0) {
        rows.push({
            resultType: "Total",
            volume: currentData?.[0]?.y_totalcount ?? 0,
            percent: "-",
            modelResult: "-",
            humanResult: "-",
            index: -1,
            id: uuidv4(),
        });
    }

    dataGridStore.setRows(rows);
    dataGridStore.setAllRows(rows);
};

const resultTypeMapping = (resultType: string) => {
    switch (resultType) {
        case "True Positive":
            return { modelResult: "True", humanResult: "True" };
        case "True Negative":
            return { modelResult: "False", humanResult: "False" };
        case "False Positive":
            return { modelResult: "True", humanResult: "False" };
        case "False Negative":
            return { modelResult: "False", humanResult: "True" };
        default:
            return { modelResult: "False", humanResult: "False" };
    }
};
